import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DialogProps {
  open: boolean;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg';
  closeOnBackdrop?: boolean;
}

export function Dialog({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  size = 'md',
  closeOnBackdrop = true,
}: DialogProps) {
  const headingId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    queueMicrotask(() => {
      const focusTarget = dialogRef.current?.querySelector<HTMLElement>(
        'input, select, textarea, button:not([data-dialog-close])',
      );
      focusTarget?.focus();
    });
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previousFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={description ? descId : undefined}
      className="fixed inset-0 z-[60] grid place-items-center px-3 py-6"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => closeOnBackdrop && onClose()}
        tabIndex={-1}
        className="absolute inset-0 size-full cursor-default bg-foreground/40 backdrop-blur-[1px]"
      />
      <div
        ref={dialogRef}
        className={cn(
          'relative w-full overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl',
          size === 'sm' && 'max-w-sm',
          size === 'md' && 'max-w-lg',
          size === 'lg' && 'max-w-3xl',
        )}
      >
        <header className="flex items-start gap-3 border-b border-border px-5 py-4">
          <div className="flex-1">
            <h2 id={headingId} className="text-sm font-semibold text-text-primary">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-0.5 text-xs text-muted-text">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            data-dialog-close
            aria-label="Close"
            onClick={onClose}
            className="-mr-1 grid size-8 place-items-center rounded-md text-muted-text transition-colors hover:bg-muted hover:text-text-primary"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>
        {children && (
          <div className="max-h-[70vh] overflow-y-auto px-5 py-4 text-sm text-text-primary">
            {children}
          </div>
        )}
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
